

import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Customers Outstanding Report Display Component
const CustomersOutstandingReportDisplay = ({ formData, filters, branchProfile, salesData }) => {
  const filteredSalesData = salesData.filter(item => {
    const itemDate = new Date(item.date.split('-').reverse().join('-')); // Convert DD-MM-YYYY to YYYY-MM-DD
    const fromDate = formData.fromDate ? new Date(formData.fromDate) : null;
    const toDate = formData.toDate ? new Date(formData.toDate) : null;

    let matchesFilters = true;
    if (fromDate) matchesFilters = matchesFilters && itemDate >= fromDate;
    if (toDate) matchesFilters = matchesFilters && itemDate <= toDate;
    if (filters.customer.length > 0 && !filters.customer.includes("All Customers")) {
      matchesFilters = matchesFilters && filters.customer.includes(item.customer || "Unknown Customer");
    }
    if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
      matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
    }
    return matchesFilters;
  });

  // Aggregate data to match the image (Opening Balance, Payable, Receivable, Closing)
  const aggregatedData = filteredSalesData.reduce((acc, item) => {
    const key = item.customer;
    if (!acc[key]) {
      acc[key] = {
        customer: item.customer || 'Unknown Customer',
        openingBalance: 0,
        payable: 0,
        receivable: 0,
        closing: 0
      };
    }
    acc[key].openingBalance += parseFloat(item.summary?.openingBalance || 0);
    acc[key].payable += parseFloat(item.summary?.netAmt || 0);
    acc[key].receivable += parseFloat(item.summary?.totalReceived || 0);
    acc[key].closing = acc[key].openingBalance + acc[key].payable - acc[key].receivable;
    return acc;
  }, {});

  const sortedData = Object.values(aggregatedData).sort((a, b) => a.customer.localeCompare(b.customer));

  const totalOpeningBalance = sortedData.reduce((sum, item) => sum + (parseFloat(item.openingBalance) || 0), 0);
  const totalPayable = sortedData.reduce((sum, item) => sum + (parseFloat(item.payable) || 0), 0);
  const totalReceivable = sortedData.reduce((sum, item) => sum + (parseFloat(item.receivable) || 0), 0);
  const totalClosing = sortedData.reduce((sum, item) => sum + (parseFloat(item.closing) || 0), 0);

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              Customers Outstanding Report
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {new Date(formData.fromDate).toLocaleDateString('en-GB')}</span>
              <span><strong>To:</strong> {new Date(formData.toDate).toLocaleDateString('en-GB')}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            {filters.customer.includes("All Customers") ? "All Customers" : filters.customer.join(", ")}
          </div>
        </div>
      </div>

      {/* Outstanding Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '11px', 
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Customer Name</th>
              {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Opening Balance</th> */}
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Payable</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Receivable</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Closing</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ 
                  border: '1px solid #cbd5e1', 
                  padding: '30px', 
                  textAlign: 'center', 
                  color: '#64748b', 
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: '#f8fafc'
                }}>
                  No outstanding data found for the selected criteria
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.customer}</td>
                  {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.openingBalance.toFixed(2)}</td> */}
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.payable.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{item.receivable.toFixed(2)}</td>
                  <td style={{ 
                    border: '1px solid #cbd5e1', 
                    padding: '8px', 
                    textAlign: 'right', 
                    fontSize: '11px',
                    color: item.closing > 0 ? '#dc2626' : '#16a34a'
                  }}>₹{item.closing.toFixed(2)}</td>
                </tr>
              ))
            )}
            
            {sortedData.length > 0 && (
              <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
                <td colSpan="2" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
                  Grand Total
                </td>
                {/* <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalOpeningBalance.toFixed(2)}</td> */}
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalPayable.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalReceivable.toFixed(2)}</td>
                <td style={{ 
                  border: '1px solid #cbd5e1', 
                  padding: '10px', 
                  textAlign: 'right', 
                  fontSize: '12px', 
                  fontWeight: '600',
                  color: totalClosing > 0 ? '#dc2626' : '#16a34a'
                }}>₹{totalClosing.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {sortedData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Customers</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{sortedData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Payable</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{totalPayable.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Receivable</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>₹{totalReceivable.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Closing</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{totalClosing.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Customers Outstanding Report Generator Component
const CustomersOutstandingReportGenerator = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [filters, setFilters] = useState({
    branch: ["All Branches"],
    customer: ["All Customers"]
  });
  const [branches, setBranches] = useState(["All Branches"]);
  const [customers, setCustomers] = useState(["All Customers"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [salesData, setSalesData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
        setBranches(["All Branches", latestProfile.branchName || "SUPREME TRADERS"]);
      }
    });

    const customersRef = ref(database, `users/${encodedEmail}/customers`);
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val();
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName);
          }
        });
      }
      setCustomers(customersData);
    });

    const salesRef = ref(database, `users/${encodedEmail}/sales`);
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const salesRecords = Object.entries(data).map(([id, sale]) => ({
          id,
          customer: sale.customer || 'Unknown Customer',
          date: sale.date || '',
          summary: {
            openingBalance: parseFloat(sale.summary?.openingBalance) || 0,
            netAmt: parseFloat(sale.summary?.netAmt) || 0,
            totalReceived: parseFloat(sale.summary?.totalReceived) || 0,
            outstanding: parseFloat(sale.summary?.outstanding) || 0
          }
        }));
        setSalesData(salesRecords);
      } else {
        setSalesData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(customersRef);
      off(salesRef);
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

 const handlePrint = () => {
  const printContent = (
    <CustomersOutstandingReportDisplay 
      formData={{ fromDate, toDate }}
      filters={filters}
      branchProfile={branchProfile}
      salesData={salesData}
    />
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Customers Outstanding Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 7px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
                padding: 4px !important;
            font-size: 12px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 60px;
          }
          /* Specific column widths for better fit */
          th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
          th:nth-child(2), td:nth-child(2) { width: 100px; } /* Customer Name */
          th:nth-child(3), td:nth-child(3) { width: 80px; } /* Payable */
          th:nth-child(4), td:nth-child(4) { width: 80px; } /* Receivable */
          th:nth-child(5), td:nth-child(5) { width: 80px; font-weight: bold; } /* Closing */
          .print-only {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Customers Outstanding Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Customers Outstanding Report </p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Customers Outstanding Report </h1>
      </div>
      
      {/* Date Range */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        whiteSpace: 'nowrap'
      }}>
        <FilterSection 
          title="Branches" 
          options={branches} 
          selected={filters.branch} 
          onChange={(value) => handleFilterChange("branch", value)}
        />
        <FilterSection 
          title="Customers" 
          options={customers} 
          selected={filters.customer} 
          onChange={(value) => handleFilterChange("customer", value)}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Customers Outstanding Report Display */}
      {showReport && (
        <CustomersOutstandingReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          salesData={salesData}
        />
      )}
    </div>
  );
};

export default CustomersOutstandingReportGenerator;